// Shared mechanics for the plane-anchored fill shapes (BOX, CIRCLE). Both
// resolve a press-time pick to a drawing plane + anchor cell, follow the laser
// to a target cell each frame, and reconcile the grid to the cells the shape
// currently covers — filling/erasing the newcomers and restoring cells that
// have left, so the whole gesture is one reversible preview.

import { type PaletteIndex, type VoxelGrid } from "../../model/voxel-grid";
import { faceNormal } from "../../model/voxel-pick";
import { isMirrorActive, mirrorCells } from "../mirror";
import { type CellBounds, type PickTarget, cellInBounds } from "../pick";

export type Cell = { ix: number; iy: number; iz: number };

// Axis-indexed cell/bounds accessors, shared by the shape handlers (box,
// circle, freeform) that all work in "plane axis + two in-plane axes" terms.
export const getAxis = (c: Cell, axis: 0 | 1 | 2): number =>
  axis === 0 ? c.ix : axis === 1 ? c.iy : c.iz;

export function setAxis(c: Cell, axis: 0 | 1 | 2, v: number): void {
  if (axis === 0) c.ix = v;
  else if (axis === 1) c.iy = v;
  else c.iz = v;
}

export const axisLo = (axis: 0 | 1 | 2, b: CellBounds): number =>
  axis === 0 ? b.minIx : axis === 1 ? b.minIy : b.minIz;
export const axisHi = (axis: 0 | 1 | 2, b: CellBounds): number =>
  axis === 0 ? b.maxIx : axis === 1 ? b.maxIy : b.maxIz;

export interface ShapePlane {
  planeAxis: 0 | 1 | 2;
  planeCoord: number;
  planeNormalSign: -1 | 1;
  startCell: Cell;
}

// Just what reconcileFill needs from a fill gesture's state. The specific cell
// set (box AABB, circle disc, freeform outline) is supplied by the caller's
// iterator; the handlers' own state types carry the extra fields they each need.
export interface ShapeFillState {
  kind:
    | "box_draw"
    | "box_erase"
    | "box_recolor"
    | "circle_draw"
    | "circle_erase"
    | "circle_recolor"
    | "freeform_draw"
    | "freeform_erase"
    | "freeform_recolor";
  applied: Map<string, Cell & { originalPalette: number }>;
  placedKeys: Set<string>;
}

export const isDrawKind = (k: ShapeFillState["kind"]): boolean =>
  k === "box_draw" || k === "circle_draw" || k === "freeform_draw";

// RECOLOR (PAINT tool) variants — set existing voxels to the active palette
// rather than fill empties / delete. Targeting matches ERASE (operate on the
// voxel under the cursor), so isDrawKind stays false for these.
export const isRecolorKind = (k: ShapeFillState["kind"]): boolean =>
  k === "box_recolor" || k === "circle_recolor" || k === "freeform_recolor";

export const cellKey = (ix: number, iy: number, iz: number): string =>
  `${ix},${iy},${iz}`;

// Resolve a press-time pick to a drawing plane + anchor cell. Returns null only
// when there's no pick at all (cursor over empty space). Both DRAW and ERASE can
// anchor on a voxel or a bare room surface — an erase box/lasso may begin on open
// canvas and delete whatever voxels fall inside the swept region.
export function planeFromPick(
  pick: PickTarget,
  action: "draw" | "erase" | "recolor",
): ShapePlane | null {
  if (!pick) return null;

  if (pick.kind === "voxel") {
    const n = faceNormal(pick.face);
    const planeAxis: 0 | 1 | 2 = n[0] !== 0 ? 0 : n[1] !== 0 ? 1 : 2;
    const planeNormalSign: -1 | 1 = (n[planeAxis] > 0 ? 1 : -1) as -1 | 1;
    const startCell =
      action === "draw"
        ? { ix: pick.ix + n[0], iy: pick.iy + n[1], iz: pick.iz + n[2] }
        : { ix: pick.ix, iy: pick.iy, iz: pick.iz };
    return { planeAxis, planeCoord: getAxis(startCell, planeAxis), planeNormalSign, startCell };
  }

  // Surface pick: DRAW fills the surface's own cell; ERASE anchors a box/lasso
  // there too, so the gesture can start on bare canvas and erase voxels swept
  // into it (nothing under the press is fine — erasing empty cells is a no-op).
  const planeAxis: 0 | 1 | 2 =
    pick.normal[0] !== 0 ? 0 : pick.normal[1] !== 0 ? 1 : 2;
  const planeNormalSign: -1 | 1 = (pick.normal[planeAxis] > 0 ? 1 : -1) as -1 | 1;
  const startCell = { ix: pick.ix, iy: pick.iy, iz: pick.iz };
  return { planeAxis, planeCoord: getAxis(startCell, planeAxis), planeNormalSign, startCell };
}

// Map a per-frame pick to the shape's target cell (BOX far corner / CIRCLE rim).
//   DRAW: the empty cell adjacent to the picked face (or a surface's own cell).
//   ERASE: the picked voxel itself; surface picks return null (nothing to erase).
export function targetCellFromPick(
  pick: PickTarget,
  draw: boolean,
): Cell | null {
  if (!pick) return null;
  if (draw) {
    if (pick.kind === "voxel") {
      const n = faceNormal(pick.face);
      return { ix: pick.ix + n[0], iy: pick.iy + n[1], iz: pick.iz + n[2] };
    }
    return { ix: pick.ix, iy: pick.iy, iz: pick.iz };
  }
  if (pick.kind !== "voxel") return null;
  return { ix: pick.ix, iy: pick.iy, iz: pick.iz };
}

// Reconcile the grid to the cells the shape currently covers (yielded by
// `cells`): fill/erase the newcomers and restore cells that have left, tracking
// everything in state.applied so a shrink retracts cleanly and release commits
// one transaction. The cell iterator is the only thing box and circle differ on.
export function reconcileFill(
  state: ShapeFillState,
  grid: VoxelGrid,
  bounds: CellBounds,
  paletteIndex: PaletteIndex,
  cells: () => Iterable<Cell>,
): void {
  const draw = isDrawKind(state.kind);
  const recolor = isRecolorKind(state.kind);

  // The shape's cells plus their mirror reflections (identity when symmetry is
  // off). Tracking these uniformly in `applied` is what makes a mirrored stroke
  // retract cleanly on shrink, even where the shape straddles the mirror plane.
  // Skip mirrorCells' per-cell array+object alloc on the common no-mirror path.
  const mirrorOn = isMirrorActive();
  const all = function* (): IterableIterator<Cell> {
    for (const c of cells()) {
      if (mirrorOn) {
        for (const m of mirrorCells(c.ix, c.iy, c.iz)) yield m;
      } else {
        yield c;
      }
    }
  };

  const desired = new Set<string>();
  for (const c of all()) desired.add(cellKey(c.ix, c.iy, c.iz));

  // Cells that have left the shape — restore them. DRAW deletes what it placed;
  // ERASE and RECOLOR both put the cell's original palette back.
  for (const [key, entry] of state.applied) {
    if (desired.has(key)) continue;
    if (draw) grid.delete(entry.ix, entry.iy, entry.iz);
    else grid.set(entry.ix, entry.iy, entry.iz, entry.originalPalette);
    state.applied.delete(key);
    state.placedKeys.delete(key);
  }

  // Cells newly inside the shape.
  for (const c of all()) {
    const key = cellKey(c.ix, c.iy, c.iz);
    if (state.applied.has(key)) continue;
    if (!cellInBounds(c.ix, c.iy, c.iz, bounds)) continue;
    if (draw) {
      if (grid.has(c.ix, c.iy, c.iz)) continue; // leave cells filled by something else
      grid.set(c.ix, c.iy, c.iz, paletteIndex);
      state.applied.set(key, { ix: c.ix, iy: c.iy, iz: c.iz, originalPalette: 0 });
      state.placedKeys.add(key);
    } else if (recolor) {
      const existing = grid.get(c.ix, c.iy, c.iz);
      if (existing === 0) continue; // only recolor existing voxels — never create
      grid.set(c.ix, c.iy, c.iz, paletteIndex);
      state.applied.set(key, { ix: c.ix, iy: c.iy, iz: c.iz, originalPalette: existing });
      state.placedKeys.add(key);
    } else {
      const existing = grid.get(c.ix, c.iy, c.iz);
      if (existing === 0) continue; // nothing to delete
      grid.delete(c.ix, c.iy, c.iz);
      state.applied.set(key, { ix: c.ix, iy: c.iy, iz: c.iz, originalPalette: existing });
      state.placedKeys.add(key);
    }
  }
}
